import { useMemo, useState } from "react";
import { useDwhData } from "../data/DwhDataContext";
import { DWH_QUERIES } from "../config/dwhQueries";
import type { DwhStackedAreaChartDef } from "../config/dwhQueries";
import { useControl } from "../control/ControlContext";
import { resampleTimeseries } from "../data/resample";
import type { TimeGrain } from "../config/plots";
import XmrChart, { KalmanToggle, type KalmanViewMode } from "./charts/XmrChart";
import EChart from "./charts/EChart";
import { ChartFrame } from "./ChartInspector";
import { computeXmr } from "../metrics/xmr";
import { computeKalman } from "../metrics/kalman";
import type { Timeseries } from "../types";
import Tooltip from "./Tooltip";
import ChartPlaceholder from "./ChartPlaceholder";

export default function DwhPlots({
  selectedTag,
  selectedOwner,
  outlierOnly,
}: {
  selectedTag: string | null;
  selectedOwner: string | null;
  outlierOnly?: boolean;
}) {
  const { dwhData, isLoading, error } = useDwhData();
  const { timeRange, grainOverride } = useControl();

  const globalGrain = useMemo((): TimeGrain => {
    if (grainOverride) return grainOverride;
    if (!timeRange.start || !timeRange.end) return "day";
    const diffDays = (timeRange.end.getTime() - timeRange.start.getTime()) / (1000 * 60 * 60 * 24);
    return diffDays > 63 ? "week" : "day";
  }, [grainOverride, timeRange]);

  const outlierChartIds = useMemo(() => {
    if (!outlierOnly || !dwhData) return null;
    const ids = new Set<string>();
    for (const queryDef of DWH_QUERIES) {
      for (const col of queryDef.columns) {
        if (col.chartType == null) continue;
        let ts = dwhData[queryDef.id]?.[col.key];
        if (!ts || ts.timestamps.length < 3) continue;
        if (globalGrain === "week") {
          ts = resampleTimeseries(ts, "week", col.resampleStrategy ?? "average");
        }
        const xmr = computeXmr(ts, { transform: col.chartType === "xmr-log" ? "log" : "identity" });
        let lastIdx = ts.timestamps.length - 1;
        while (lastIdx >= 0 && ts.data[lastIdx] == null) lastIdx--;
        if (lastIdx < 0) continue;
        const lastTimestamp = ts.timestamps[lastIdx];
        const isOutlier = xmr.anomalies.some(a => lastTimestamp >= a.start && lastTimestamp <= a.end);
        if (isOutlier) ids.add(`dwh-${queryDef.id}-${col.key}`);
      }
    }
    return ids;
  }, [outlierOnly, dwhData, globalGrain]);

  if (error)
    return (
      <div className="plots" style={{ color: "crimson" }}>
        DWH error: {error}
      </div>
    );

  if (isLoading || !dwhData) {
    return (
      <div className="plots">
        {DWH_QUERIES.flatMap(q => [
          ...q.columns
            .filter(col => col.chartType != null)
            .filter(col => {
              if (selectedTag && !(col.tags ?? []).includes(selectedTag)) return false;
              if (selectedOwner && col.owner !== selectedOwner) return false;
              return true;
            })
            .map(col => {
              const chartId = `chart-dwh-${q.id}-${col.key}`;
              return (
                <div key={chartId} id={chartId}>
                  <h3>
                    {q.label}: {col.label}
                  </h3>
                  <ChartPlaceholder />
                </div>
              );
            }),
          ...(q.stackedAreaCharts ?? []).map(chart => {
            const chartId = `chart-dwh-${q.id}-${chart.id}`;
            return (
              <div key={chartId} id={chartId}>
                <h3>{chart.label}</h3>
                <ChartPlaceholder />
              </div>
            );
          }),
        ])}
      </div>
    );
  }

  return (
    <div className="plots">
      {DWH_QUERIES.flatMap(queryDef => [
        ...queryDef.columns
          .filter(col => {
            if (col.chartType == null) return false;
            if (selectedTag && !(col.tags ?? []).includes(selectedTag)) return false;
            if (selectedOwner && col.owner !== selectedOwner) return false;
            if (outlierChartIds && !outlierChartIds.has(`dwh-${queryDef.id}-${col.key}`)) return false;
            return true;
          })
          .map(col => {
            const rawTs = dwhData[queryDef.id]?.[col.key];
            if (!rawTs) return null;
            const ts =
              globalGrain === "week" ? resampleTimeseries(rawTs, "week", col.resampleStrategy ?? "average") : rawTs;
            const chartId = `dwh-${queryDef.id}-${col.key}`;
            return (
              <DwhChartSection
                key={chartId}
                id={chartId}
                title={`${queryDef.label}: ${col.label}`}
                ts={ts}
                unit={col.unit}
                logScale={col.chartType === "xmr-log"}
                description={col.description}
                grain={globalGrain}
              />
            );
          }),
        ...(queryDef.stackedAreaCharts ?? []).map(chartDef => {
          if (outlierChartIds) return null;
          const seriesCols = chartDef.series.map(s => queryDef.columns.find(c => c.key === s.columnKey));
          if (selectedTag && !seriesCols.some(c => (c?.tags ?? []).includes(selectedTag))) return null;
          if (selectedOwner && !seriesCols.some(c => c?.owner === selectedOwner)) return null;
          const rawSeriesData = chartDef.series.map(s => dwhData[queryDef.id]?.[s.columnKey]);
          if (rawSeriesData.some(ts => ts == null)) return null;
          const seriesData =
            globalGrain === "week"
              ? rawSeriesData.map((rawTs, i) => {
                  const col = queryDef.columns.find(c => c.key === chartDef.series[i].columnKey);
                  return resampleTimeseries(rawTs as Timeseries, "week", col?.resampleStrategy ?? "average");
                })
              : (rawSeriesData as Timeseries[]);
          return (
            <StackedAreaDwhChart
              key={`dwh-${queryDef.id}-${chartDef.id}`}
              id={`dwh-${queryDef.id}-${chartDef.id}`}
              queryId={queryDef.id}
              chartDef={chartDef}
              seriesData={seriesData}
            />
          );
        }),
      ])}
    </div>
  );
}

interface DwhChartSectionProps {
  id: string;
  title: string;
  ts: Timeseries;
  unit?: string;
  logScale?: boolean;
  description?: string;
  grain?: "day" | "week";
}

interface StackedAreaDwhChartProps {
  id: string;
  queryId: string;
  chartDef: DwhStackedAreaChartDef;
  seriesData: Timeseries[];
}

function StackedAreaDwhChart({ id, chartDef, seriesData }: StackedAreaDwhChartProps) {
  const option = useMemo(() => {
    const timestamps = seriesData[0].timestamps.map(t => t.toISOString().slice(0, 10));
    return {
      tooltip: { trigger: "axis", valueFormatter: (v: number) => `${v}%` },
      legend: { data: chartDef.series.map(s => s.label) },
      xAxis: { type: "category", data: timestamps, boundaryGap: false },
      yAxis: { type: "value", min: 0, max: 100, axisLabel: { formatter: (v: number) => `${v}%` } },
      series: chartDef.series.map((s, i) => ({
        name: s.label,
        type: "line",
        stack: "total",
        areaStyle: { color: s.color },
        lineStyle: { color: s.color },
        itemStyle: { color: s.color },
        smooth: true,
        symbol: "none",
        data: seriesData[i].data.map(v => v ?? null),
      })),
    };
  }, [chartDef, seriesData]);

  const combinedTs: Timeseries = {
    timestamps: seriesData[0].timestamps,
    data: seriesData[0].data.map((v, i) => (v != null && seriesData[1].data[i] != null ? 100 : null)),
  };

  return (
    <div id={`chart-${id}`}>
      <h3>{chartDef.label}</h3>
      <ChartFrame title={chartDef.label} data={combinedTs} chartLabel="Stacked area chart">
        <EChart option={option} />
      </ChartFrame>
    </div>
  );
}

function DwhChartSection({ id, title, ts, unit, logScale, description, grain }: DwhChartSectionProps) {
  const [kalmanMode, setKalmanMode] = useState<KalmanViewMode>("standard");

  const xmr = useMemo(() => {
    if (ts.timestamps.length < 3) return null;
    return computeXmr(ts, { transform: logScale ? "log" : "identity" });
  }, [ts, logScale]);

  const kalman = useMemo(() => {
    if (!xmr || grain === "week") return undefined;
    return computeKalman(xmr.ts, { Q: 0.1 * xmr.sigma ** 2, R: xmr.sigma ** 2 });
  }, [xmr, grain]);

  const axisFormatter = useMemo(() => {
    if (unit === "%") return (v: number) => `${Math.round(v)}%`;
    if (unit === "EUR")
      return (v: number) => {
        const abs = Math.abs(v);
        if (abs >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}M`;
        if (abs >= 1_000) return `€${(v / 1_000).toFixed(0)}k`;
        return `€${Math.round(v)}`;
      };
    return undefined;
  }, [unit]);

  if (!xmr) return null;

  return (
    <div id={`chart-${id}`}>
      <h3>
        {description ? (
          <Tooltip content={description} align="left">
            {title}
          </Tooltip>
        ) : (
          title
        )}
      </h3>
      <ChartFrame
        title={title}
        data={ts}
        chartLabel="XmR chart"
        axisFormatter={axisFormatter}
        headerExtra={kalman ? <KalmanToggle activeMode={kalmanMode} onModeChange={setKalmanMode} /> : undefined}
      >
        <XmrChart
          xmr={xmr}
          kalman={kalman}
          viewMode={kalman ? kalmanMode : undefined}
          axisFormatter={axisFormatter}
          axisName={unit}
        />
      </ChartFrame>
    </div>
  );
}
